<template>
  <div>
    <div class="m-field-group m-field-group--inline filters" style="margin-top: 0px">
      <div class="grid-x grid-padding-x">
        <div class="cell large-12 s-content-block s-content-block--small">
          <h5>Country</h5>
          <multiselect
            v-if="search == 'to'"
            id="filterCountrySelect"
            v-model="selectedCountryProxy"
            @select="filterCountrySelected"
            :options="filterCountries"
            :multiple="false"
            :custom-label="countryMultiselectCustomLabel"
            track-by="id"
            :placeholder="`Select Country`"
            :preselect-first="false"
            :showLabels="false"
            :disabled="!filterCountries || !filterCountries.length"
            :loading="!filterCountries || !filterCountries.length"
            autocomplete="off"
          >
            <template slot="option" slot-scope="props">
              <span :title="countryMultiselectCustomLabel(props.option)">{{
                countryMultiselectCustomLabel(props.option)
              }}</span>
            </template>
          </multiselect>
          <div v-else>
            <input
              type="text"
              value="BC (including Yukon University and Athabasca University)"
              aria-label="BC (including Yukon University and Athabasca University)"
              title="BC (including Yukon University and Athabasca University)"
              readonly
              disabled
              class="disabled-country-input"
              style="background-color: #d2d2d2"
            />
          </div>
        </div>
      </div>

      <div class="grid-x grid-padding-x">
        <div class="cell large-5 s-content-block s-content-block--small">
          <h5>Filter by Type</h5>
          <div class="m-checkboxes">
            <div>
              <input
                type="checkbox"
                name="institution-type"
                id="allInstitutionTypes"
                v-model="allInstitutionTypesChecked"
                @change="handleInstitutionTypesChange('all')"
                v-on:input="$event.target.checked = true"
              />
              <label for="allInstitutionTypes">All institution types</label>
            </div>
            <div v-for="(institutionType, index) in institutionTypes" :key="institutionType.id">
              <input
                type="checkbox"
                name="institution-type"
                :id="`it-${institutionType.id}`"
                :value="institutionType.id"
                :disabled="institutionType.disabled"
                v-model="institutionTypes[index].checked"
                @change="handleInstitutionTypesChange('institution', index)"
              />
              <label :for="`it-${institutionType.id}`">{{ institutionType.name }}</label>
            </div>
            <div>
              <input
                type="checkbox"
                name="institution-type"
                id="it-private"
                v-model="privateInstitutionTypeChecked"
                :disabled="
                  !(
                    selectedCountryProxy &&
                    (selectedCountryProxy.id === 'BC' || selectedCountryProxy.id === 40)
                  )
                "
                @change="handleInstitutionTypesChange('private')"
              />
              <label for="it-private">Private Institutions</label>
            </div>
          </div>
        </div>
        <div class="cell large-7 s-content-block s-content-block--small">
          <h5 id="InstitutionMultiselect">Institution</h5>
          <div class="m-checkboxes m-checkboxes--has-background institution-select">
            <div class="m-checkboxes__inner">
              <div v-if="institutions.length !== 0">
                <input
                  type="checkbox"
                  name="institution"
                  :id="'allInstitutions'"
                  v-model="allInstitutionsChecked"
                  @change="handleInstitutionsChange('all')"
                  v-on:input="$event.target.checked = true"
                />
                <label for="allInstitutions">Any</label>
              </div>

              <div v-if="institutions.length === 0">
                <p>No institutions available</p>
              </div>
              <div v-for="(institution, index) in institutions" :key="index">
                <div v-if="!institution.hidden">
                  <input
                    type="checkbox"
                    :id="`institution_checkbox_${institution.id}`"
                    :value="institution.id"
                    :disabled="institution.disabled"
                    v-model="institutions[index].checked"
                    :checked="institution.checked || institutions.length === 1"
                    @change="handleInstitutionsChange('institution', index)"
                  />
                  <label :for="`institution_checkbox_${institution.id}`">
                    {{
                      ['Canada', 'United States of America'].includes(institution.country)
                        ? institution.title + ' (' + institution.province + ')'
                        : institution.title
                    }}
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div class="m-selected-items" v-if="!allInstitutionsChecked">
            <div v-for="institution in institutions" :key="institution.id">
              <div v-if="institution.checked" class="a-selected-item">
                {{ institution.code }}
                <span @click="removeSelectedInstitution(institution)" class="fa fa-close"> </span>
              </div>
            </div>
          </div>
          <div
            class="institution_any a-text-small"
            v-if="allInstitutionsChecked && institutions.length !== 0"
          >
            Any institution has been selected.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, defineExpose, ref, onMounted, computed } from 'vue'
import Multiselect from 'vue-multiselect'
import { Observable } from 'rxjs'
import { share } from 'rxjs/operators'
import axios from 'axios'
import _ from 'lodash'

const props = defineProps({
  nonce: {
    required: true,
  },
  isOnResultsPage: {
    default: false,
  },
  search: {
    default: 'from',
    required: true,
  },
  filter: {
    type: Object,
    required: true,
  },
  searchCustomBaseURL: {
    type: String,
    required: true,
  },
})

const countryBC = ref(null)
const filterCountries = ref([])
const countryCanada = ref(null)
const countryUSA = ref(null)
const filterSelectedCountry = ref({
  id: 'BC',
  countryName: 'BC (including Yukon University and Athabasca University)',
})
const countryObserver = ref(null)
const institutionObserver = ref(null)
const institutionTypesObserver = ref(null)
const institutionInputOptions = ref([])
const institutionTypesInputOptions = ref([])
const institutions = ref([])
const institutionTypes = ref([])
const allInstitutionTypesChecked = ref(false)
const privateInstitutionTypeChecked = ref(false)
const allInstitutionsChecked = ref(false)
const institutionMultiselectComponentSelectedOptions = ref([])
const institutionTypesMultiselectComponentSelectedOptions = ref([])

const updatefilterCountries = (filterCountries) => {
  countryBC.value = _.first(
    _.filter(
      filterCountries,
      (country) =>
        _.get(country, 'countryName') ===
        'BC (including Yukon University and Athabasca University)',
    ),
  )
  countryCanada.value = _.first(
    _.filter(filterCountries, (country) => _.get(country, 'countryName') === 'Canada'),
  )
  countryUSA.value = _.first(
    _.filter(
      filterCountries,
      (country) => _.get(country, 'countryName') === 'United States of America',
    ),
  )
  if (!filterSelectedCountry.value) {
    filterSelectedCountry.value = countryBC.value
  }
  return filterCountries
}

const fetchCountries = () => {
  return new Observable((subscriber) => {
    if (filterCountries.value && filterCountries.value.length) {
      filterCountries.value = updatefilterCountries(filterCountries.value)
      subscriber.next(filterCountries.value)
      subscriber.complete()
      return
    }
    let countries = sessionStorage.getItem('courseToCourseCountries')
    if (_.isString(countries)) {
      countries = JSON.parse(countries)
    }
    if (countries) {
      filterCountries.value = updatefilterCountries(countries)
      subscriber.next(filterCountries.value)
      subscriber.complete()
      return
    } else {
      axios.get(props.searchCustomBaseURL + 'agreementws/GetCountries').then((response) => {
        var canada = response.data.filter((el) => el.countryShortName == 'Canada')
        var usa = response.data.filter((el) => el.countryShortName == 'USA')
        let bc = JSON.parse(JSON.stringify(canada))
        bc[0].id = 'BC'
        bc[0].countryName = 'BC (including Yukon University and Athabasca University)'
        bc[0].countryShortName = 'BC'
        bc[0].alpha2 = 'BC'
        bc[0].alpha3 = 'BC'
        response.data.unshift(usa[0])
        response.data.unshift(canada[0])
        response.data.unshift(bc[0])
        filterCountries.value = updatefilterCountries(response.data)
        subscriber.next(filterCountries.value)
        subscriber.complete()
        return
      })
    }
  }).pipe(share())
}

const fetchInstitutionTypes = () => {
  return new Observable((subscriber) => {
    institutionTypes.value = []
    axios.get(props.searchCustomBaseURL + 'agreementws/GetInstitutionTypes?').then((response) => {
      let institutionTypesData = response.data.filter((el) => el.isActive)

      if (institutionTypes.value.length === 0) {
        institutionTypesData = institutionTypesData.map((institutionType) => {
          return {
            ...institutionType,
            checked: false,
            disabled: false,
          }
        })

        institutionTypes.value = institutionTypesData
        institutionTypesInputOptions.value = institutionTypesData
        subscriber.next(institutionTypesData)
      } else {
        subscriber.next(institutionTypes.value)
      }
      subscriber.complete()
    })
  }).pipe(share())
}

const fetchInstitutions = (countryId = 'BC', isSecondCountryDropdownChanged = false) => {
  return new Observable((subscriber) => {
    if (!countryId) {
      countryId = filterSelectedCountry.value.id
    }

    if (props.filter.fromOrToInstitution == 'to' && !isSecondCountryDropdownChanged) {
      countryId = props.filter.selectedSecondCountry.id
    }

    if (institutions.value && institutions.value.length) {
      if (institutions.value.some((x) => x.countryId === countryId)) {
        subscriber.next(institutions.value)
        subscriber.complete()
      } else {
        institutions.value = []
      }
    }

    axios
      .get(props.searchCustomBaseURL + 'agreementws/GetFromInstitutions', {
        params: {
          countryId: countryId == 'BC' ? 40 : countryId,
          internalOnly: countryId == 'BC' ? 'true' : 'false',
        },
      })
      .then((response) => {
        let institutionsData = response.data
        if (institutions.value.length === 0) {
          _.forEach(institutionsData, (institution, index) => {
            _.set(institutionsData, [index, 'hidden'], false)
            _.set(institutionsData, [index, 'checked'], false)
            _.set(institutionsData, [index, 'disabled'], false)
          })
          institutions.value = institutionsData
          institutionInputOptions.value = institutionsData
          subscriber.next(institutionsData)
        } else {
          subscriber.next(institutions.value)
        }
        subscriber.complete()
        updateFilteredInstitutions()
      })
  }).pipe(share())
}

const setInstitutionCountryChange = (countryId) => {
  return new Promise((resolve, reject) => {
    filterSelectedCountry.value = countryId

    institutions.value.forEach((institution, index) => {
      if (!institution.approveForNonMember && countryId != 'BC') {
        _.set(institutions.value, [index, 'disabled'], true)
      } else {
        _.set(institutions.value, [index, 'disabled'], false)
      }
    })

    if (institutions.value.length === 1) {
      _.set(institutions.value[0], 'disabled', true)
    }

    resolve()
  })
}

const filterCountrySelected = (country) => {
  institutions.value = []

  fetchInstitutions(country.id, true).subscribe({
    next: (institutionsData) => {
      institutions.value = institutionsData.map((institution) => ({
        ...institution,
        disabled: !institution.approveForNonMember && country.id !== 'BC',
      }))
    },
    error: (err) => {},
    complete: () => {},
  })

  emitCountryChangeEvent(country)

  if (country.id === filterSelectedCountry.value.id || country.id === 40 || country.id === 'BC') {
    institutionTypes.value = institutionTypes.value.map((institutionType, index) => {
      return {
        ...institutionType,
        disabled: false,
      }
    })
  } else {
    allInstitutionTypesChecked.value = true

    institutionTypes.value = institutionTypes.value.map((institutionType, index) => {
      return {
        ...institutionType,
        disabled: true,
      }
    })
  }
}

const countryMultiselectCustomLabel = (data) => {
  if (data.countryShortName) {
    return _.get(data, 'countryShortName')
  } else {
    return _.get(data, 'countryName')
  }
}

const setCountry = (country) => {
  return new Promise((resolve, reject) => {
    filterSelectedCountry.value = country
    resolve()
  })
}

const setInstitutionTypes = (institutionTypes = [], isPrivateSelected = false) => {
  return new Promise((resolve, reject) => {
    if (institutionTypes === null || institutionTypes === undefined) {
      institutionTypes = []
    }

    const setInstitutionTypesIds = institutionTypes.map((institutionType) => institutionType.id)

    if (
      institutionTypesObserver.value &&
      typeof institutionTypesObserver.value.subscribe === 'function'
    ) {
      institutionTypesObserver.value.subscribe((institutionTypes) => {
        const institutionTypesMultiselectComponentSelectedOptions = institutionTypes
          .map((institutionType) => {
            institutionType.checked = setInstitutionTypesIds.includes(institutionType.id)
            return institutionType.checked ? institutionType : null
          })
          .filter(Boolean)

        institutionTypes.value = institutionTypes

        const checkedInstitutionTypes = institutionTypes.filter(
          (institution) => institution.checked,
        )

        if (checkedInstitutionTypes.length > 0) {
          allInstitutionTypesChecked.value = false
        }

        institutionTypesInputOptions.value = institutionTypes.value
        institutionTypesMultiselectComponentSelectedOptions.value =
          institutionTypesMultiselectComponentSelectedOptions

        if (filterSelectedCountry.value) {
          const isSameCountry =
            filterSelectedCountry.value.id === 'BC' || filterSelectedCountry.value.id === 40

          allInstitutionTypesChecked.value = !isSameCountry

          institutionTypes.value.forEach((institutionType, index) => {
            institutionType.disabled = !isSameCountry
          })
        }

        privateInstitutionTypeChecked.value = isPrivateSelected

        resolve()
      })
    } else {
      reject(
        new Error('institutionTypesObserver is not initialized or subscribe is not a function'),
      )
    }
  })
}

const institutionTypesMultiselectComponentOnSelect = (selectedOption, id) => {
  let ids = map(institutionTypesMultiselectComponentSelectedOptions.value, (institutionType) =>
    get(institutionType, 'id'),
  )
  let selectedOptionId = get(selectedOption, 'id')
  ids.push(selectedOptionId)

  updateInstitutionTypesCheckboxes(ids)
  updateFilteredInstitutions()
}

const institutionTypesMultiselectComponentOnRemove = (removedOption, id) => {
  let ids = map(institutionTypesMultiselectComponentSelectedOptions.value, (institutionType) =>
    get(institutionType, 'id'),
  )
  let removedOptionId = get(removedOption, 'id')

  ids = filter(ids, (id) => id != removedOptionId)

  updateInstitutionTypesCheckboxes(ids)
  updateFilteredInstitutions()
}

const updateInstitutionTypesCheckboxes = (ids) => {
  institutionTypes.value = institutionTypes.value.map((institutionType, index) => {
    return {
      ...institutionType,
      checked: ids.includes(institutionType.id),
    }
  })
}

const setInstitutions = (institutions = []) => {
  return new Promise((resolve, reject) => {
    if (institutions === null || institutions === undefined) {
      institutions = []
    }

    const setInstitutionsIds = institutions.map((institution) => institution.id)

    allInstitutionsChecked.value = false

    if (institutionObserver.value && typeof institutionObserver.value.subscribe === 'function') {
      institutionObserver.value.subscribe((institutions) => {
        const institutionMultiselectComponentSelectedOptions = institutions
          .map((institution, index) => {
            const isChecked = setInstitutionsIds.includes(institution.id)
            Object.assign(institutions[index], { checked: isChecked })
            return isChecked ? institution : null
          })
          .filter(Boolean)

        institutions.value = institutions.value

        const checkedInstitutions = institutions.filter((institution) => institution.checked)

        if (checkedInstitutions.length > 0) {
          allInstitutionsChecked.value = false
        }

        institutionInputOptions.value = institutions.value
        institutionMultiselectComponentSelectedOptions.value =
          institutionMultiselectComponentSelectedOptions

        resolve()
      })
    } else {
      reject(new Error('institutionObserver is not initialized or subscribe is not a function'))
    }
  })
}

const institutionMultiselectComponentOnSelect = (selectedOption, id) => {
  let ids = map(institutionMultiselectComponentSelectedOptions.value, (institution) =>
    get(institution, 'id'),
  )
  let selectedOptionId = get(selectedOption, 'id')
  ids.push(selectedOptionId)

  updateInstitutionCheckboxes(ids)
  updateFilteredInstitutions()
}

const institutionMultiselectComponentOnRemove = (removedOption, id) => {
  let ids = map(institutionMultiselectComponentSelectedOptions.value, (institution) =>
    get(institution, 'id'),
  )
  let removedOptionId = get(removedOption, 'id')

  ids = filter(ids, (id) => id != removedOptionId)

  updateInstitutionCheckboxes(ids)
  updateFilteredInstitutions()
}

const updateInstitutionCheckboxes = (ids) => {
  institutions.value = institutions.value.map((institution, index) => {
    return {
      ...institution,
      checked: ids.includes(institution.id),
    }
  })
}

const handleInstitutionTypesChange = (type, index = null) => {
  if (type === 'all') {
    allInstitutionTypesChecked.value = true

    institutionTypes.value = institutionTypes.value.map((institutionType, index) => {
      return {
        ...institutionType,
        checked: false,
      }
    })
  } else {
    allInstitutionTypesChecked.value = false
  }
  updateFilteredInstitutions()
}

const handleInstitutionsChange = (type, index = null) => {
  if (type === 'all') {
    allInstitutionsChecked.value = true
  } else {
    allInstitutionsChecked.value = false
  }
  updateFilteredInstitutions()
}

const updateFilteredInstitutions = () => {
  const institutionTypesChecked = _.filter(institutionTypes.value, (institutionType) =>
    _.get(institutionType, 'checked'),
  )

  if (allInstitutionTypesChecked.value || institutionTypesChecked.length == 0) {
    if (privateInstitutionTypeChecked.value === false) {
      allInstitutionTypesChecked.value = true
    }
  } else {
    allInstitutionTypesChecked.value = false
  }

  institutionTypesMultiselectComponentSelectedOptions.value = institutionTypesChecked
  if (allInstitutionTypesChecked.value) {
    _.forEach(institutions.value, (institution, index) => {
      _.set(institutions.value, [index, 'hidden'], false)
    })
    privateInstitutionTypeChecked.value = false
  } else {
    const checkedInstitutionTypes = _.map(
      _.filter(institutionTypes.value, (institutionType) => _.get(institutionType, 'checked')),
      (institutionType) => institutionType.id,
    )
    institutions.value.forEach((institution, index) => {
      const institutionTypeId = _.get(institution, 'systemInstitutionTypeId')
      const isPrivate = !_.get(institution, 'systemIsPublic')
      const isSelectedInstitutionType = checkedInstitutionTypes.includes(institutionTypeId)
      let isVisible = false
      if (
        isSelectedInstitutionType ||
        (!checkedInstitutionTypes.length && privateInstitutionTypeChecked.value)
      ) {
        isVisible = true
        if (privateInstitutionTypeChecked.value) {
          isVisible = isVisible && isPrivate
        }
      }
      _.set(institutions.value, [index, 'hidden'], !isVisible)
      if (!isVisible) {
        _.set(institutions.value, [index, 'checked'], false)
      }
    })
  }
  if (allInstitutionsChecked.value) {
    _.forEach(institutions.value, (institution, index) => {
      _.set(institutions.value, [index, 'checked'], false)
    })

    var checkedInstitutions = _.filter(
      _.clone(institutions.value),
      (institution) => institution.checked,
    )

    var visibleInstitutions = _.filter(
      _.clone(institutions.value),
      (institution) => !institution.hidden,
    )
  } else {
    var checkedInstitutions = _.filter(
      _.clone(institutions.value),
      (institution) => institution.checked,
    )

    var visibleInstitutions = _.filter(
      _.clone(institutions.value),
      (institution) => !institution.hidden,
    )

    if (visibleInstitutions.length == checkedInstitutions.length) {
      _.forEach(institutions.value, (institution, index) => {
        _.set(institutions.value, [index, 'checked'], false)
      })
    }
  }

  if (checkedInstitutions.length == 0 || visibleInstitutions.length == checkedInstitutions.length) {
    allInstitutionsChecked.value = true
  }

  institutionMultiselectComponentSelectedOptions.value = checkedInstitutions
  var institutionsForSenderorReceivers = _.filter(
    _.clone(institutions.value),
    (institution) => !institution.hidden,
  )

  emitChangeEvent(checkedInstitutions)
  emitChangeEventInstitutionType(institutionTypesChecked)
  emitChangeEventPrivateInstitutionType(privateInstitutionTypeChecked.value)
  getInstitutionsForSendersAndReceivers(institutionsForSenderorReceivers)
}

const removeSelectedInstitution = (removeInstitution) => {
  _.forEach(institutions.value, (institution, index) => {
    if (removeInstitution.id === institution.id) {
      _.set(institutions.value, [index, 'checked'], false)
    }
  })
  updateFilteredInstitutions()
}

const emitChangeEvent = (institutions) => {
  emit('institutionsChanged', institutions)
}

const emitChangeEventInstitutionType = (institutionTypesChecked) => {
  emit('institutionTypesChanged', institutionTypesChecked)
}

const emitChangeEventPrivateInstitutionType = (privateInstitutionTypeChecked) => {
  emit('privateInstitutionTypeChanged', privateInstitutionTypeChecked)
}

const emitCountryChangeEvent = (country) => {
  emit('countryChanged', country)
}

const getInstitutionsForSendersAndReceivers = (institutions) => {
  emit('institutionsForSenderorReceiversChanged', institutions)
}

const emit = defineEmits([
  'institutionsChanged',
  'institutionTypesChanged',
  'privateInstitutionTypeChanged',
  'countryChanged',
  'institutionsForSenderorReceiversChanged',
])

defineExpose({
  setInstitutionCountryChange,
  setCountry,
  setInstitutionTypes,
  setInstitutions,
  emitChangeEvent,
  emitChangeEventInstitutionType,
  emitChangeEventPrivateInstitutionType,
  emitCountryChangeEvent,
  getInstitutionsForSendersAndReceivers,
})

onMounted(() => {
  countryObserver.value = fetchCountries()
  institutionTypesObserver.value = fetchInstitutionTypes()
  institutionObserver.value = fetchInstitutions()

  countryObserver.value.subscribe(() => {
    institutionTypesObserver.value.subscribe(() => {
      institutionObserver.value.subscribe()
    })
  })
})

const selectedCountryProxy = computed({
  get() {
    // Always resolve the correct reference from filterCountries using props.filter.selectedSecondCountry.id
    if (
      props.filter.selectedSecondCountry &&
      props.filter.selectedSecondCountry.id &&
      filterCountries.value &&
      filterCountries.value.length > 0
    ) {
      const matched = filterCountries.value.find(
        (country) => country.id === props.filter.selectedSecondCountry.id,
      )
      return matched || filterCountries.value[0]
    }
    return filterCountries.value[0] || null
  },
  set(val) {
    // Update props.filter.selectedSecondCountry to the selected value
    if (val && val.id) {
      props.filter.selectedSecondCountry = val
    }
  },
})
</script>

<style>
.m-field-group--inline {
  margin: 20px 0;
}
</style>
